import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { HelpCircle, Layers, LucideIcon, PenLine, Trophy } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface TopAuthor {
  id: number
  fullname: string
  count: number
}

// Shape of GET api/authors/top
export interface TopAuthors {
  editors: TopAuthor[]
  questions: TopAuthor[]
  themes: TopAuthor[]
}

const CATEGORIES: { key: keyof TopAuthors; label: string; path: string; icon: LucideIcon }[] = [
  { key: 'questions', label: 'NHN',    path: 'questions', icon: HelpCircle },
  { key: 'editors',   label: 'Redaktə', path: 'edits',     icon: PenLine },
  { key: 'themes',    label: 'Mövzu',   path: 'themes',    icon: Layers },
]

const RANK_BADGE = [
  'bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300',
  'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  'bg-orange-100 text-orange-700 dark:bg-orange-900/60 dark:text-orange-300',
]

const AUTO_SLIDE_MS = 6000
const SWIPE_THRESHOLD_PX = 40

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

/** Top authors per contribution type; auto-slides, with tabs, swipe and arrow keys for manual control. */
const TopAuthorsCarousel: React.FC<{ data: TopAuthors | undefined }> = ({ data }) => {
  const slides = CATEGORIES.filter(c => data === undefined || data[c.key]?.length > 0)
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  // Bumped on manual navigation so the auto-slide timer restarts from zero
  const [timerKey, setTimerKey] = useState(0)
  const touchX = useRef<number | null>(null)

  const count = slides.length
  const active = count > 0 ? index % count : 0

  const goTo = useCallback((i: number) => {
    setIndex(((i % count) + count) % count)
    setTimerKey(k => k + 1)
  }, [count])

  useEffect(() => {
    if (paused || count < 2 || data === undefined || prefersReducedMotion()) return
    const id = setInterval(() => setIndex(i => (i + 1) % count), AUTO_SLIDE_MS)
    return () => clearInterval(id)
  }, [paused, count, data, timerKey])

  if (count === 0) return null

  return (
    <div
      className="h-full bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm rounded-2xl px-4 py-3 space-y-2 overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={e => {
        if (e.key === 'ArrowRight') goTo(active + 1)
        if (e.key === 'ArrowLeft') goTo(active - 1)
      }}
      role="region"
      aria-roledescription="carousel"
      aria-label="Top müəlliflər"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-300">
          <Trophy className="w-3 h-3" /> Top müəlliflər
        </span>
        <div className="flex items-center gap-0.5 rounded-lg bg-gray-100 dark:bg-gray-800 p-0.5" role="tablist">
          {slides.map((c, i) => (
            <button
              key={c.key}
              type="button"
              role="tab"
              aria-selected={i === active}
              onClick={() => goTo(i)}
              className={cn(
                'appearance-none !rounded-md px-2 py-0.5 text-[11px] font-semibold transition-colors',
                i === active
                  ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              )}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div
        className="overflow-hidden touch-pan-y"
        onTouchStart={e => { touchX.current = e.touches[0].clientX }}
        onTouchEnd={e => {
          if (touchX.current === null) return
          const dx = e.changedTouches[0].clientX - touchX.current
          touchX.current = null
          if (Math.abs(dx) > SWIPE_THRESHOLD_PX) goTo(active + (dx < 0 ? 1 : -1))
        }}
      >
        <div
          className="flex transition-transform duration-500 ease-out motion-reduce:transition-none"
          style={{ transform: `translateX(-${active * 100}%)` }}
        >
          {slides.map((c, i) => {
            const authors = data?.[c.key]
            const hidden = i !== active
            const Icon = c.icon
            return (
              <ol key={c.key} className="w-full flex-shrink-0 space-y-1" role="tabpanel" aria-hidden={hidden} aria-label={c.label}>
                {authors === undefined
                  ? Array.from({ length: 5 }, (_, r) => (
                      <li key={r} className="animate-pulse flex items-center gap-2 py-1">
                        <span className="h-5 w-5 rounded-full bg-gray-100 dark:bg-gray-800" />
                        <span className="h-3 flex-1 rounded-full bg-gray-100 dark:bg-gray-800" />
                      </li>
                    ))
                  : authors.map((a, r) => (
                      <li key={a.id}>
                        <Link
                          to={`/authors/${a.id}/${c.path}`}
                          tabIndex={hidden ? -1 : undefined}
                          className="group flex items-center gap-2 rounded-lg -mx-1.5 px-1.5 py-0.5 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <span className={cn(
                            'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
                            RANK_BADGE[r] ?? 'bg-gray-50 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                          )}>
                            {r + 1}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-800 dark:text-gray-200 group-hover:text-blue-500 dark:group-hover:text-indigo-200">
                            {a.fullname}
                          </span>
                          <span className="inline-flex flex-shrink-0 items-center gap-1 text-xs font-semibold text-gray-500 dark:text-gray-400">
                            {a.count}
                            <Icon className="w-3 h-3 opacity-60" />
                          </span>
                        </Link>
                      </li>
                    ))}
              </ol>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default TopAuthorsCarousel
